import React from 'react';
import { Link } from 'react-router-dom';
import DashboardCard, { DashboardLink, TABLE_HEAD_CLASS } from './DashboardCard';

const times = (value) => (typeof value === 'number' ? `${value.toFixed(2)}x` : '—');

// rows: utils/ipoDashboard.js buildDashboardSubscriptions
export const SubscriptionTrackerCard = ({ board, rows, loading, openCount = 0, error, onRetry, query = '' }) => {
  const isSme = board.key === 'sme';
  const label = `${isSme ? 'SME ' : ''}IPO${openCount === 1 ? '' : 's'}`;
  return (
    <DashboardCard
      title="Live IPO Subscription Tracker"
      titleAddon={<span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />}
      subtitle={`Bidding demand across QIB, NII/HNI, and ${isSme ? 'individual' : 'retail'} investor segments`}
      aside={<span className="text-[11px] font-medium text-slate-400 whitespace-nowrap">NSE Bid Book</span>}
      footer={
        <DashboardLink to={isSme ? '/sme-ipo-subscription' : '/ipo-subscription'}>
          View Full Live Subscription Breakdown
        </DashboardLink>
      }
    >
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className={TABLE_HEAD_CLASS}>
              <th className="py-3 pl-4 sm:pl-5 pr-3 min-w-[190px]">Company</th>
              <th className="py-3 px-3 text-right whitespace-nowrap">QIB (x)</th>
              <th className="py-3 px-3 text-right whitespace-nowrap">NII / HNI (x)</th>
              <th className="py-3 px-3 text-right whitespace-nowrap">{isSme ? 'Individual' : 'Retail'} (x)</th>
              <th className="py-3 px-3 text-right whitespace-nowrap bg-emerald-50 text-emerald-700">Total (x)</th>
              <th className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
            {loading &&
              Array.from({ length: 3 }).map((_, idx) => (
                <tr key={`skeleton-${idx}`} className="animate-pulse">
                  <td className="py-3 pl-4 sm:pl-5 pr-3"><div className="h-4 w-40 rounded bg-slate-200" /></td>
                  {Array.from({ length: 5 }).map((__, col) => (
                    <td key={col} className="py-3 px-3"><div className="ml-auto h-4 w-12 rounded bg-slate-100" /></td>
                  ))}
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-sm text-slate-500">
                  {error ? (
                    <>
                      Live subscription data is temporarily unavailable.{' '}
                      <button type="button" onClick={onRetry} className="font-bold text-[#008080] hover:underline">
                        Retry
                      </button>
                    </>
                  ) : query.trim() ? (
                    `No live subscription figures for “${query.trim()}”.`
                  ) : openCount ? (
                    `${openCount} ${label} ${openCount === 1 ? 'is' : 'are'} open, but the exchange feed has no bidding figures for ${openCount === 1 ? 'it' : 'them'} yet.`
                  ) : (
                    `No ${isSme ? 'SME ' : ''}IPO is taking bids right now. Figures appear here from 10:00 AM IST on an issue’s first day.`
                  )}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="py-3 pl-4 sm:pl-5 pr-3 font-semibold text-slate-800">
                    {row.symbol ? (
                      <Link to={`/ipo-subscription/${encodeURIComponent(row.symbol)}`} className="hover:text-[#008080] hover:underline">
                        {row.company}
                      </Link>
                    ) : (
                      row.company
                    )}
                    {row.closed && <span className="ml-1.5 text-[10px] font-bold text-slate-400">(Closed)</span>}
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-600">{times(row.qib)}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-600">{times(row.nii)}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-600">{times(row.retail)}</td>
                  <td className="py-3 px-3 text-right tabular-nums font-extrabold text-emerald-700 bg-emerald-50/70">{times(row.total)}</td>
                  <td className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap text-[11px] text-slate-400">{row.updated}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </DashboardCard>
  );
};

export default SubscriptionTrackerCard;
